import React, { useState, useEffect, useCallback } from 'react';
import { formatUSD, formatDate, formatFXAmount } from '../utils/keyGenerator';

/* ============================================
   STATUS CONFIGURATION
   ============================================ */
const STATUS_CONFIG = {
  INITIATED:          { label: 'Initiated',         icon: '\u25CB', color: '#6B7280', bg: 'rgba(107,114,128,0.06)' },
  PROCESSING:         { label: 'Processing',        icon: '\u25D4', color: '#3B82F6', bg: 'rgba(59,130,246,0.06)' },
  COMPLIANCE_CHECK:   { label: 'Compliance',        icon: '\u2611', color: '#F59E0B', bg: 'rgba(245,158,11,0.06)' },
  SETTLING:           { label: 'Settling',          icon: '\u21C4', color: '#8B5CF6', bg: 'rgba(139,92,246,0.06)' },
  SETTLED:            { label: 'Settled',           icon: '\u2713', color: '#0D9488', bg: 'rgba(13,148,136,0.06)' },
  SWIFT_DISPATCHED:   { label: 'SWIFT Dispatched',  icon: '\u2709', color: '#0284C7', bg: 'rgba(2,132,199,0.06)' },
  RECEIVER_CONFIRMED: { label: 'Confirmed',         icon: '\u2714', color: '#059669', bg: 'rgba(5,150,105,0.06)' },
  FAILED:             { label: 'Failed',            icon: '\u2717', color: '#EF4444', bg: 'rgba(239,68,68,0.06)' },
};
const STATUS_ORDER = ['INITIATED', 'PROCESSING', 'COMPLIANCE_CHECK', 'SETTLING', 'SETTLED', 'SWIFT_DISPATCHED', 'RECEIVER_CONFIRMED'];

/* ============================================
   PROGRESS BAR
   ============================================ */
function StatusProgress({ currentStatus, size }) {
  const currentIdx = STATUS_ORDER.indexOf(currentStatus);
  const failed = currentStatus === 'FAILED';
  const sz = size === 'sm' ? { node: 14, active: 18, font: 8, activeFontSize: 11, bar: 2 }
    : { node: 20, active: 30, font: 10, activeFontSize: 15, bar: 3 };
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0, width: '100%' }}>
      {STATUS_ORDER.map((s, i) => {
        const cfg = STATUS_CONFIG[s];
        const isActive = !failed && i <= currentIdx;
        const isCurrent = !failed && i === currentIdx;
        return (
          <React.Fragment key={s}>
            <div style={{
              width: isCurrent ? sz.active : sz.node, height: isCurrent ? sz.active : sz.node, borderRadius: '50%',
              background: failed ? '#EF4444' : isActive ? cfg.color : 'var(--border)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'white', fontSize: isCurrent ? sz.activeFontSize : sz.font, fontWeight: 700,
              transition: 'all 0.4s ease', flexShrink: 0,
              boxShadow: isCurrent ? `0 0 12px ${cfg.color}50` : 'none',
            }} title={cfg.label}>{isActive ? cfg.icon : ''}</div>
            {i < STATUS_ORDER.length - 1 && (
              <div style={{
                flex: 1, height: sz.bar, borderRadius: 2,
                background: !failed && i < currentIdx ? STATUS_CONFIG[STATUS_ORDER[i + 1]].color : 'var(--border)',
                transition: 'all 0.4s ease',
              }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

/* ============================================
   TIME AGO (live ticking)
   ============================================ */
function TimeAgo({ date }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    const iv = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(iv);
  }, []);
  const sec = Math.floor((Date.now() - new Date(date).getTime()) / 1000);
  if (sec < 60) return <span>{sec}s ago</span>;
  if (sec < 3600) return <span>{Math.floor(sec / 60)}m {sec % 60}s ago</span>;
  return <span>{Math.floor(sec / 3600)}h {Math.floor((sec % 3600) / 60)}m ago</span>;
}

/* ============================================
   METHOD BADGE
   ============================================ */
function MethodBadge({ type, mt }) {
  const label = mt || type || 'TRF';
  const isCredit = type === 'DEP' || type === 'LEDGER';
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: '3px 10px', borderRadius: 5, letterSpacing: '0.04em',
      background: isCredit ? 'var(--teal-glow)' : 'var(--gold-glow)',
      color: isCredit ? 'var(--teal)' : 'var(--gold)',
      border: `1px solid ${isCredit ? 'rgba(13,148,136,0.2)' : 'rgba(218,165,32,0.2)'}`,
    }}>{label}</span>
  );
}

/* ============================================
   MAIN COMPONENT
   ============================================ */
export default function LiveTracker({ transactions }) {
  const [trackerData, setTrackerData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('ALL');
  const [expandedId, setExpandedId] = useState(null);
  const [search, setSearch] = useState('');

  // Fetch live tracker data from server
  const fetchTracker = useCallback(() => {
    fetch('/api/tracker/transactions?limit=100')
      .then(r => r.ok ? r.json() : [])
      .then(data => { setTrackerData(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    fetchTracker();
    const iv = setInterval(fetchTracker, 2000);
    return () => clearInterval(iv);
  }, [fetchTracker]);

  // Build a lookup from tracker data by txnRef
  const trackerByRef = {};
  trackerData.forEach(t => { trackerByRef[t.txnRef] = t; });

  // Only show DEBIT transactions (outbound — the ones that get tracked)
  const debitTxns = (transactions || []).filter(t => t.debitAmount);

  // Merge ledger data with tracker data
  const merged = debitTxns.map(tx => {
    const tracker = trackerByRef[tx.id];
    return {
      ...tx,
      tracker,
      liveStatus: tracker?.currentStatus || 'RECEIVER_CONFIRMED',
      statusHistory: tracker?.statusHistory || [
        { status: 'INITIATED', timestamp: tx.date, detail: 'Transaction initiated by sender' },
        { status: 'PROCESSING', timestamp: tx.date, detail: 'Transaction validated' },
        { status: 'COMPLIANCE_CHECK', timestamp: tx.date, detail: 'Sanctions & AML screening — CLEAR' },
        { status: 'SETTLING', timestamp: tx.date, detail: 'Settlement in progress' },
        { status: 'SETTLED', timestamp: tx.date, detail: 'Settlement finalized — irrevocable' },
        { status: 'SWIFT_DISPATCHED', timestamp: tx.date, detail: 'SWIFT message dispatched via Dwin' },
        { status: 'RECEIVER_CONFIRMED', timestamp: tx.date, detail: 'Receiver bank confirmed credit' },
      ],
      uetr: tracker?.uetr || tx.id,
      trackingId: tracker?.trackingId || `TRK-${tx.id}`,
      onChainHash: tracker?.onChainHash || null,
      swiftMessageId: tracker?.swiftMessageId || null,
      complianceResult: tracker?.complianceResult || null,
    };
  });

  // Filter
  const searchLower = search.toLowerCase();
  const filtered = merged.filter(tx => {
    if (filter === 'ACTIVE' && tx.liveStatus === 'RECEIVER_CONFIRMED') return false;
    if (filter === 'ACTIVE' && tx.liveStatus === 'FAILED') return false;
    if (filter === 'CONFIRMED' && tx.liveStatus !== 'RECEIVER_CONFIRMED') return false;
    if (filter === 'FAILED' && tx.liveStatus !== 'FAILED') return false;
    if (searchLower) {
      return (tx.counterparty || '').toLowerCase().includes(searchLower) ||
             (tx.id || '').toLowerCase().includes(searchLower) ||
             (tx.description || '').toLowerCase().includes(searchLower);
    }
    return true;
  });

  const activeCount = merged.filter(t => t.liveStatus !== 'RECEIVER_CONFIRMED' && t.liveStatus !== 'FAILED').length;
  const confirmedCount = merged.filter(t => t.liveStatus === 'RECEIVER_CONFIRMED').length;
  const failedCount = merged.filter(t => t.liveStatus === 'FAILED').length;

  const totalDebitValue = debitTxns.reduce((s, t) => s + (t.debitAmount || 0), 0);

  return (
    <div className="gap-16">
      {/* Live Banner */}
      <div className="settle-bar">
        <div className="settle-dot" />
        <span className="settle-text">LIVE TRANSACTION TRACKER</span>
        <span className="settle-time">Auto-refresh 2s &middot; {new Date().toLocaleTimeString()}</span>
      </div>

      {/* Stats */}
      <div className="grid4">
        <div className="stat-tile blue">
          <div className="stat-label">Outbound Sent</div>
          <div className="stat-value">{merged.length}</div>
          <div className="stat-sub">{formatUSD(totalDebitValue)} total</div>
        </div>
        <div className="stat-tile gold">
          <div className="stat-label">In Progress</div>
          <div className="stat-value" style={{ color: activeCount > 0 ? 'var(--gold)' : 'var(--text3)' }}>{activeCount}</div>
          <div className="stat-sub">{activeCount > 0 ? 'Processing now' : 'None active'}</div>
        </div>
        <div className="stat-tile teal">
          <div className="stat-label">Confirmed</div>
          <div className="stat-value stat-green">{confirmedCount}</div>
          <div className="stat-sub">Receiver confirmed</div>
        </div>
        <div className="stat-tile coral">
          <div className="stat-label">Failed</div>
          <div className="stat-value stat-red">{failedCount}</div>
          <div className="stat-sub">Rejected / error</div>
        </div>
      </div>

      {/* Filter + Search Bar */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        {[
          { id: 'ALL', label: `All (${merged.length})` },
          { id: 'ACTIVE', label: `In Progress (${activeCount})` },
          { id: 'CONFIRMED', label: `Confirmed (${confirmedCount})` },
          { id: 'FAILED', label: `Failed (${failedCount})` },
        ].map(f => (
          <button key={f.id} className={`btn btn-sm ${filter === f.id ? 'btn-gold' : 'btn-outline'}`}
            onClick={() => setFilter(f.id)}>
            {f.label}
          </button>
        ))}
        <input
          className="form-input"
          style={{ marginLeft: 'auto', maxWidth: 260, padding: '7px 14px', fontSize: 12 }}
          placeholder="Search by name, reference..."
          value={search} onChange={e => setSearch(e.target.value)}
        />
      </div>

      {/* Active Cards (in-progress only) */}
      {(filter === 'ALL' || filter === 'ACTIVE') && merged.filter(t => t.liveStatus !== 'RECEIVER_CONFIRMED' && t.liveStatus !== 'FAILED').length > 0 && (
        <div className="card">
          <div className="card-header">
            <span className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              In Progress
              <span style={{ width: 10, height: 10, borderRadius: '50%', background: 'var(--teal)', animation: 'pulse 1.5s ease-in-out infinite' }} />
            </span>
          </div>
          <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {merged.filter(t => t.liveStatus !== 'RECEIVER_CONFIRMED' && t.liveStatus !== 'FAILED').map(tx => {
              const cfg = STATUS_CONFIG[tx.liveStatus] || STATUS_CONFIG.INITIATED;
              const lastH = tx.statusHistory[tx.statusHistory.length - 1];
              return (
                <div key={tx.id} style={{
                  padding: '20px 24px', borderRadius: 'var(--radius-lg)',
                  border: `1.5px solid ${cfg.color}30`, background: cfg.bg,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
                    <div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--navy)' }}>{tx.counterparty}</div>
                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 5 }}>
                        <MethodBadge type={tx.type} mt={tx.mt} />
                        <span style={{ fontSize: 11, color: 'var(--text3)', fontFamily: "'IBM Plex Mono', monospace" }}>{tx.id}</span>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--coral)' }}>
                        {tx.originalCurrency && tx.originalCurrency !== 'USD'
                          ? formatFXAmount(tx.originalAmount, tx.originalCurrency)
                          : formatUSD(tx.debitAmount)}
                      </div>
                      {tx.originalCurrency && tx.originalCurrency !== 'USD' && (
                        <div style={{ fontSize: 11, color: 'var(--text3)' }}>({formatUSD(tx.debitAmount)} USD)</div>
                      )}
                    </div>
                  </div>

                  {/* Progress */}
                  <div style={{ marginBottom: 4 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                      {STATUS_ORDER.map(s => (
                        <div key={s} style={{ fontSize: 8, color: 'var(--text3)', textAlign: 'center', flex: 1, fontWeight: 600, letterSpacing: '0.03em' }}>
                          {STATUS_CONFIG[s].label}
                        </div>
                      ))}
                    </div>
                    <StatusProgress currentStatus={tx.liveStatus} />
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span style={{ fontSize: 18, color: cfg.color }}>{cfg.icon}</span>
                      <span style={{ fontSize: 13, fontWeight: 600, color: cfg.color }}>{cfg.label}</span>
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text3)' }}>
                      <TimeAgo date={tx.tracker?.updatedAt || tx.date} />
                    </div>
                  </div>

                  {lastH && (
                    <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 10, padding: '8px 12px', background: 'rgba(255,255,255,0.6)', borderRadius: 6 }}>
                      {lastH.detail}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* All Transactions Table */}
      {filtered.length > 0 && (
        <div className="card">
          <div className="card-header">
            <span className="card-title">
              {filter === 'CONFIRMED' ? 'Confirmed' : filter === 'FAILED' ? 'Failed' : filter === 'ACTIVE' ? 'In Progress' : 'All Outbound'} Transactions
            </span>
            <span style={{ fontSize: 11, color: 'var(--text3)' }}>{filtered.length} transaction{filtered.length !== 1 ? 's' : ''}</span>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            <table>
              <thead>
                <tr>
                  <th>Reference</th>
                  <th>Counterparty</th>
                  <th>Method</th>
                  <th>Amount</th>
                  <th>Key Test</th>
                  <th>Live Status</th>
                  <th style={{ minWidth: 130 }}>Progress</th>
                  <th>Updated</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(tx => {
                  const cfg = STATUS_CONFIG[tx.liveStatus] || STATUS_CONFIG.SETTLED;
                  const isExpanded = expandedId === tx.id;
                  return (
                    <React.Fragment key={tx.id}>
                      <tr style={{ cursor: 'pointer' }} onClick={() => setExpandedId(isExpanded ? null : tx.id)}>
                        <td>
                          <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: 'var(--navy)', fontWeight: 600 }}>
                            {tx.id}
                          </div>
                          <div style={{ fontSize: 10, color: 'var(--text3)' }}>{formatDate(tx.date)}</div>
                        </td>
                        <td style={{ fontWeight: 600 }}>{tx.counterparty}</td>
                        <td><MethodBadge type={tx.type} mt={tx.mt} /></td>
                        <td style={{ fontWeight: 600, color: 'var(--coral)' }}>
                          {tx.originalCurrency && tx.originalCurrency !== 'USD'
                            ? formatFXAmount(tx.originalAmount, tx.originalCurrency)
                            : formatUSD(tx.debitAmount)}
                          {tx.originalCurrency && tx.originalCurrency !== 'USD' && (
                            <div style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 400 }}>{formatUSD(tx.debitAmount)} USD</div>
                          )}
                        </td>
                        <td>
                          <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 12, color: 'var(--gold)', fontWeight: 700, letterSpacing: '0.1em' }}>
                            {tx.keyTest}
                          </span>
                        </td>
                        <td>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 600, color: cfg.color }}>
                            <span style={{ fontSize: 14 }}>{cfg.icon}</span>
                            {cfg.label}
                          </span>
                        </td>
                        <td><StatusProgress currentStatus={tx.liveStatus} size="sm" /></td>
                        <td style={{ fontSize: 11, color: 'var(--text3)', whiteSpace: 'nowrap' }}>
                          <TimeAgo date={tx.tracker?.updatedAt || tx.date} />
                        </td>
                        <td>
                          <span style={{ fontSize: 12, color: 'var(--text3)' }}>{isExpanded ? '\u25B2' : '\u25BC'}</span>
                        </td>
                      </tr>

                      {/* Expanded Row */}
                      {isExpanded && (
                        <tr>
                          <td colSpan={9} style={{ padding: '20px 24px', background: 'var(--surface2)' }}>
                            <div className="grid2" style={{ gap: 24 }}>
                              {/* Details */}
                              <div>
                                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.15em', color: 'var(--gold)', marginBottom: 10 }}>
                                  TRANSACTION DETAILS
                                </div>
                                {[
                                  ['Reference', tx.id],
                                  ['Description', tx.description],
                                  ['Counterparty', tx.counterparty],
                                  ['Beneficiary', tx.beneficiary || tx.counterparty],
                                  ['Bank', tx.bankName || 'N/A'],
                                  ['BIC', tx.receiverBIC || tx.bankBIC || 'N/A'],
                                  ['Currency', tx.originalCurrency || tx.currency || 'USD'],
                                  ['Amount (USD)', formatUSD(tx.debitAmount)],
                                  ['Key Test', tx.keyTest],
                                  ['UETR', tx.uetr || 'N/A'],
                                  ['Tracking ID', tx.trackingId || 'N/A'],
                                  ['On-Chain Hash', tx.onChainHash ? tx.onChainHash.slice(0, 24) + '...' : 'N/A'],
                                  ['SWIFT Message', tx.swiftMessageId || 'N/A'],
                                  ['Compliance', tx.complianceResult ? `Sanctions: ${tx.complianceResult.sanctions} | AML: ${tx.complianceResult.aml}` : 'Passed'],
                                  ['Balance After', formatUSD(tx.balanceAfter)],
                                ].map(([k, v]) => (
                                  <div key={k} style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', borderBottom: '1px dotted var(--border-light)', fontSize: 12 }}>
                                    <span style={{ color: 'var(--text3)', minWidth: 100 }}>{k}</span>
                                    <span style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: 'var(--navy)', fontWeight: 500, textAlign: 'right', maxWidth: '60%', wordBreak: 'break-all' }}>{v}</span>
                                  </div>
                                ))}
                              </div>

                              {/* Timeline */}
                              <div>
                                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.15em', color: 'var(--gold)', marginBottom: 10 }}>
                                  STATUS TIMELINE
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column' }}>
                                  {tx.statusHistory.map((h, i) => {
                                    const hCfg = STATUS_CONFIG[h.status] || STATUS_CONFIG.INITIATED;
                                    const isLast = i === tx.statusHistory.length - 1;
                                    return (
                                      <div key={i} style={{ display: 'flex', gap: 12 }}>
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 18 }}>
                                          <div style={{
                                            width: isLast ? 14 : 10, height: isLast ? 14 : 10, borderRadius: '50%', flexShrink: 0,
                                            background: hCfg.color,
                                            boxShadow: isLast ? `0 0 8px ${hCfg.color}50` : 'none',
                                          }} />
                                          {!isLast && <div style={{ width: 2, flex: 1, background: 'var(--border)', minHeight: 18 }} />}
                                        </div>
                                        <div style={{ paddingBottom: isLast ? 0 : 10, flex: 1 }}>
                                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <span style={{ fontSize: 12, fontWeight: 600, color: hCfg.color }}>{hCfg.label}</span>
                                            <span style={{ fontSize: 10, color: 'var(--text3)', fontFamily: "'IBM Plex Mono', monospace" }}>
                                              {new Date(h.timestamp).toLocaleTimeString()}
                                            </span>
                                          </div>
                                          <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 2, lineHeight: 1.4 }}>{h.detail}</div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && merged.length === 0 && (
        <div className="card">
          <div className="card-body" style={{ padding: 60, textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 16, opacity: 0.2 }}>{'\u25C9'}</div>
            <div style={{ fontFamily: "'Source Serif 4', serif", fontSize: 20, color: 'var(--navy)', fontWeight: 600 }}>
              No Outbound Transactions
            </div>
            <p style={{ color: 'var(--text3)', fontSize: 13, marginTop: 8, maxWidth: 420, margin: '8px auto 0' }}>
              Send a payment via API, KTT, RTGS, or POBO SWIFT and it will appear here with real-time status tracking through the full settlement lifecycle.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
